/**
 * Expirations client seam (docs/11 F-3, partial).
 *
 * A read only, and deliberately thin: the windows, the lane split and the
 * scoping all live on the server (see `modules/expirations/service.ts`). docs/11
 * F-3 records the failure this avoids by name — v1 ran "a server bundle and a
 * client hook with different windows", so the same lease was expiring on one
 * screen and fine on another. This hook filters nothing.
 */

import type { ExpirationKind, ExpirationsResponse } from '@propos/shared';
import { useQuery } from '@tanstack/react-query';

import { api } from '../../app/api.js';

export type { ExpirationKind, ExpirationRow, ExpirationsResponse } from '@propos/shared';

export interface ExpirationsParams {
  readonly scope?: 'mine' | 'all';
  readonly lane?: 'all' | 'expired' | 'soon';
  readonly kinds?: readonly ExpirationKind[];
  readonly propertyId?: string;
  readonly limit?: number;
}

function toQuery(params: ExpirationsParams): string {
  const search = new URLSearchParams();
  if (params.scope !== undefined) search.set('scope', params.scope);
  if (params.lane !== undefined) search.set('lane', params.lane);
  if (params.kinds !== undefined && params.kinds.length > 0) {
    search.set('kinds', params.kinds.join(','));
  }
  if (params.propertyId !== undefined) search.set('propertyId', params.propertyId);
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  return search.toString();
}

export function useExpirations(params: ExpirationsParams = {}) {
  const query = toQuery(params);
  return useQuery({
    queryKey: ['expirations', query],
    queryFn: () => api<ExpirationsResponse>(`/api/v1/expirations?${query}`),
    /**
     * Every filter change is a new query key, so without this the list unmounts
     * to a skeleton on each click — which read as "the data just went missing"
     * while Matt was exploring the chips. Holding the previous result keeps the
     * rows on screen until the new ones arrive.
     */
    placeholderData: (previous) => previous,
  });
}
